import React, { ReactNode, useContext } from "react";
import styled, { css } from "styled-components";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useSearchInput from "utils/hooks/useSearchInput";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { formatDate, getFormatTime } from "@shared/utils/date";

interface TablePanelProps {
  title: ReactNode;
  count?: number;
  /** The table; it gets `searchFilter` from the panel's search. */
  table: React.JSX.Element;
  /** First load: skeleton rows instead of a spinner over an empty table. */
  loading?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * A table in a flush panel: the title, count and search live in the panel
 * header, the rows run edge to edge (56px, hairlines, a calm hover).
 */
const TablePanel = ({
  title,
  count,
  table,
  loading,
  className,
  "data-testid": testId,
}: TablePanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { useMobileSearch, search } = useContext(PageWrapperContext);
  const [searchInput, searchValue] = useSearchInput({
    placeholder: t("labels.search_placeholder"),
    style: { width: 220 },
    lowerCaseValue: true,
  });

  return (
    <Panel
      className={className}
      title={title}
      count={count || undefined}
      actions={!useMobileSearch && searchInput}
      flush={true}
      data-testid={testId}
    >
      {loading ? (
        <SkeletonRows aria-busy={true}>
          {Array.from({ length: 5 }, (_, index) => (
            <SkeletonRow key={index}>
              <Stack>
                <Skeleton width={96} height={12} />
                <Skeleton width={40} height={10} />
              </Stack>
              <Skeleton width="40%" height={12} />
              <Skeleton width={64} height={12} />
            </SkeletonRow>
          ))}
        </SkeletonRows>
      ) : (
        <TableBody>
          {React.cloneElement(table, {
            searchFilter: useMobileSearch ? search.value : searchValue,
          })}
        </TableBody>
      )}
    </Panel>
  );
};

export default TablePanel;

/** A date over its time, the table's first column. */
export const DateCell = ({ time }: { time: number }) => (
  <Stack>
    <CellText>{formatDate(time, false, true)}</CellText>
    <CellMeta>{getFormatTime(time, true)}</CellMeta>
  </Stack>
);

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
export const CellText = styled.span<{ $ink?: boolean; $truncate?: boolean }>`
  ${newLookType.body};
  color: ${(p) =>
    p.$ink ? getNewLookTokens(p.theme).ink : getNewLookTokens(p.theme).text};
  white-space: nowrap;
  ${(p) =>
    p.$truncate &&
    css`
      display: block;
      max-width: 320px;
      overflow: hidden;
      text-overflow: ellipsis;
      @media (max-width: 1199.98px) {
        max-width: 200px;
      }
    `}
`;
export const CellMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
export const CellFigure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 34px;
`;
const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr) auto;
  align-items: center;
  gap: 24px;
  height: 60px;
  padding: 0 20px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
/* Column headers come from the global table styles (micro role); this only
   sets the panel's edges, the 56px row rhythm and the hover (the same
   treatment as the other new-look panel tables). */
const TableBody = styled.div`
  position: relative;
  min-width: 0;
  .ant-table-wrapper .ant-table {
    background: transparent;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    height: 60px;
    padding: 8px 12px;
    line-height: 20px;
    white-space: nowrap;
    transition: background-color 120ms;
  }
  .ant-table-wrapper .ant-table-thead > tr > th {
    padding: 10px 12px;
    white-space: nowrap;
  }
  .ant-table-wrapper .ant-table-thead > tr > th:first-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:first-child {
    padding-left: 20px;
  }
  .ant-table-wrapper .ant-table-thead > tr > th:last-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:last-child {
    padding-right: 16px;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td.ant-table-column-sort {
    background: transparent !important;
  }
  && .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td,
  &&
    .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row
    > .ant-table-cell-row-hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  /* An empty table is just its empty state under the panel header. */
  .ant-table-wrapper .ant-table-empty .ant-table-thead {
    display: none;
  }
  /* The empty state sits in the panel, not in a bordered table cell. */
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-placeholder > td {
    height: auto;
    padding: 0;
    border-bottom: 0;
    background: transparent !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent !important;
  }
  .ant-table-wrapper .ant-table-pagination.ant-pagination {
    margin: 0;
    padding: 12px 16px;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  /* Copy icons show on the row under the pointer (and on focus). */
  @media (hover: hover) {
    .ant-table-tbody .nl-copy > svg {
      opacity: 0;
    }
    .ant-table-tbody > tr:hover .nl-copy > svg,
    .ant-table-tbody .nl-copy:focus-visible > svg {
      opacity: 1;
    }
  }
  /* Sort arrows sit right after the column title (before it on the
     right-aligned figure columns). */
  .ant-table-wrapper .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 4px;
    .ant-table-column-title {
      flex: none;
    }
  }
  .ant-table-wrapper .ant-table-thead th.nl-number .ant-table-column-sorters {
    justify-content: flex-end;
  }
`;
